import React, { useState } from 'react'
import { IContextMenuState } from 'types'
import Button from 'components/button'
import Popover, { popoverPlacement } from 'components/popover'

const WIDTH = 280

interface CountPopoverProps {
  /** What's counted, as the cell says it: "3 events". */
  count: string
  /** What the list is, for screen readers: "Events". */
  title: string
  /** The webhook it belongs to, for screen readers: "3 events of Zapier sync". */
  of: string
  id: string
  /** How many rows the list has, to place the popover where it fits. */
  rows: number
  /** The list, one thing per line. */
  children: React.ReactNode
}

/**
 * A count in a webhook's row, "3 events", underlined, opening a popover that lists what's counted, as the
 * audit log's "N changes" opens its change.
 */
const CountPopover: React.FC<CountPopoverProps> = ({ count, title, of, id, rows, children }) => {
  const [at, setAt] = useState<IContextMenuState | null>(null)
  return (
    <>
      <Button
        internalType="transparent"
        aria-haspopup="dialog"
        aria-expanded={!!at}
        aria-label={`${count} of ${of}`}
        className="h-auto p-0 font-normal text-body underline underline-offset-2 decoration-body/40 hover:bg-transparent hover:text-body hover:decoration-body"
        // The popover closes on a press outside it, and this button counts as outside: while it's open, keep the
        // press from reaching it and let the click close it instead.
        onMouseDown={(event: React.MouseEvent) => {
          if (at) event.stopPropagation()
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAt(
            at
              ? null
              : popoverPlacement(event.currentTarget.getBoundingClientRect(), {
                  align: 'start',
                  width: WIDTH,
                  height: rows * 24 + 60
                })
          )
        }
      >
        {count}
      </Button>
      <Popover id={id} at={at} onClose={() => setAt(null)} label={`${title} of ${of}`} title={count} width={WIDTH}>
        <ul className="flex max-h-[280px] flex-col gap-1 overflow-auto">{children}</ul>
      </Popover>
    </>
  )
}

/** A line of a CountPopover's list in monospace, as an event's or a header's name. */
export const CodeItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="font-mono text-[12px] leading-5 break-all text-body">{children}</li>
)

export default CountPopover
